import { createWebHistory, createRouter } from "vue-router";
import routes from './routes';
import appConfig from "../../app.config";
import store from '@/state/store';
import {
  ACCOUNTING_ROLES,
  AUDIT_LOG_ROLES,
  PRIVATE_DEBT_ROLES,
  PRODUCTION_PAGE_ROLES,
  PARTNER_DEBT_ROLES,
  RECEIVABLE_ROLES,
  SALES_ROLES,
  confinedRedirect,
  confinesOnPublicRoutes,
  isAdminRole,
  roleAllowed,
} from '@/constants/roles';

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    // Trang giữ trạng thái trên query (vd. ?ticket= mở popup) ⇒ chỉ đổi query thì đứng yên.
    if (to.meta.keepScrollOnQuery && to.path === from.path) {
      return false;
    }
    return { top: 0, left: 0 };
  },
});

// Authentication guard
// Checks if user is authenticated before accessing protected routes
router.beforeEach(async (to, from, next) => {
  const authRequired = to.matched.some((route) => route.meta.authRequired);
  const accessToken = store.state.auth.accessToken;

  // If user is already logged in and trying to access login page, redirect to home.
  // Confined roles will then be redirected from "/" to their own area below.
  if (!authRequired && to.path === '/login' && store.getters['auth/loggedIn']) {
    return next('/');
  }

  // Public pages remain public for anonymous visitors. An authenticated visitor must
  // continue through the role confinement check, including on /demo and TV screens.
  if (!authRequired && !accessToken) return next();
  if (!accessToken) {
    return next({
      path: '/login',
      query: { redirect: to.fullPath }
    });
  }

  // Token exists - validate and fetch user if not already loaded
  if (!store.state.auth.currentUser) {
    try {
      await store.dispatch('auth/fetchCurrentUser');
    } catch (error) {
      // A stale token must not turn a public page into a protected page. Protected
      // routes keep the existing behavior and return to login.
      if (!authRequired) return next();
      return next({
        path: '/login',
        query: { redirect: to.fullPath }
      });
    }
  }

  const roleName = store.state.auth.currentUser?.role_name;

  // Route công khai: chỉ role có `confinePublicRoutes` (hiện là quản đốc) mới phải
  // đi tiếp xuống `confinedRedirect` bên dưới. Kế toán/thủ kho giữ nguyên hành vi cũ,
  // xem được các trang công khai (TV xưởng, trang demo khách) như trước khi có role
  // quản đốc — không âm thầm bị đá theo luật khoá vùng mới thêm cho role khác.
  if (!authRequired && !confinesOnPublicRoutes(roleName)) return next();

  // Role bị khoá vùng (bảng `CONFINED_ROLE_AREAS` trong constants/roles.cjs). Mọi
  // route ngoài vùng đó đá về trang chủ vùng của chính họ.
  //
  // Đặt TRƯỚC khối requiresAdmin bên dưới để tránh vòng lặp redirect giữa hai khối
  // (nếu đặt sau: role bị requiresAdmin đá về "/" trước, rồi guard chạy lại từ đầu
  // cho "/", khối này lại đá về trang chủ khu vực — vẫn dừng đúng 1 vòng nhờ thứ tự
  // hiện tại, nhưng nếu đảo ngược thứ tự hai khối sẽ tạo vòng lặp).
  //
  // Luật khoá vùng nằm trong `confinedRedirect` (constants/roles.cjs, có test): chép
  // khối `if` này thêm một lần cho mỗi role là chỗ dễ sai nhất — quên một dấu `!` là
  // vòng lặp redirect, triệu chứng chỉ là trang trắng và không chỉ về dòng code nào cả.
  //
  // Hàm đó cũng giữ danh sách route AI CŨNG VÀO ĐƯỢC (/pages/profile-setting, /logout):
  // thiếu nó thì kế toán không đổi được mật khẩu của chính mình, vì hai đường dẫn đó
  // không nằm trong /accounting/.
  const redirectTo = confinedRedirect(roleName, to.path);
  if (redirectTo) {
    return next(redirectTo);
  }

  // Authenticated users without a confined role can now enter the public route.
  if (!authRequired) return next();

  // Route chỉ dành cho admin (tổ may / quản lý & thống kê nhân viên / tồn đầu kỳ).
  const requiresAdmin = to.matched.some((route) => route.meta.requiresAdmin);
  if (requiresAdmin && !isAdminRole(roleName)) {
    return next('/');
  }

  // Phần nhà cung cấp của module kế toán: ALLOW-LIST (kế toán + admin). Kế toán đã
  // được cho qua ở khối trên vì to.path đã bắt đầu bằng /accounting/; khối này chặn
  // mọi role còn lại.
  const requiresAccounting = to.matched.some((route) => route.meta.requiresAccounting);
  if (requiresAccounting && !roleAllowed(roleName, ACCOUNTING_ROLES)) {
    return next('/');
  }

  // Công nợ khách hàng + thanh toán đơn: ACCOUNTING_ROLES + thư ký. Thư ký đã được
  // `confinedRedirect` phía trên giữ trong đúng 2 route này + công nợ CTV.
  const requiresReceivables = to.matched.some((route) => route.meta.requiresReceivables);
  if (requiresReceivables && !roleAllowed(roleName, RECEIVABLE_ROLES)) {
    return next('/');
  }

  // Tiến độ sản xuất: ai có ít nhất một tab (Đơn cần giao: admin, quản đốc, vận đơn;
  // Chuyền sản xuất: admin, quản đốc, staff — chốt 2026-10-26). Trang tự ẩn tab không
  // được phép. Quản đốc còn được `confinedRedirect` phía trên giữ trong đúng khu vực
  // này; allow-list ở đây chặn mọi role còn lại.
  const requiresDeliverySchedule = to.matched.some(
    (route) => route.meta.requiresDeliverySchedule,
  );
  if (requiresDeliverySchedule && !roleAllowed(roleName, PRODUCTION_PAGE_ROLES)) {
    return next('/');
  }

  // Công nợ CTV: thư ký + super_admin — allow-list KHÔNG có admin thường, gương của
  // `PARTNER_DEBT_ROLES` ở backend (app/core/roles.py). Thư ký đã được
  // `confinedRedirect` phía trên giữ trong đúng khu vực này; khối này chặn mọi role
  // còn lại, kể cả admin. Đừng thêm ngoại lệ admin cho "nhất quán" — backend trả 403.
  // Module bán hàng (vd /zalo/care): admin + nhân viên — gương `require_sales_or_admin`.
  const requiresSales = to.matched.some((route) => route.meta.requiresSales);
  if (requiresSales && !roleAllowed(roleName, SALES_ROLES)) {
    return next('/');
  }

  const requiresAuditLog = to.matched.some((route) => route.meta.requiresAuditLog);
  if (requiresAuditLog && !roleAllowed(roleName, AUDIT_LOG_ROLES)) {
    return next('/');
  }

  const requiresPrivateDebt = to.matched.some((route) => route.meta.requiresPrivateDebt);
  if (requiresPrivateDebt && !roleAllowed(roleName, PRIVATE_DEBT_ROLES)) {
    return next('/');
  }

  const requiresPartnerDebt = to.matched.some((route) => route.meta.requiresPartnerDebt);
  if (requiresPartnerDebt && !roleAllowed(roleName, PARTNER_DEBT_ROLES)) {
    return next('/');
  }

  next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + ' | ' + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
